import { useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { User, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAvatarColor, getInitials } from '@/lib/avatar'

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

interface OwnerPickerProps {
  value: string | null
  teamMembers: TeamMember[]
  onChange: (userId: string | null) => void
  disabled?: boolean
}

interface MemberAvatarProps {
  member: TeamMember
  size?: 'sm' | 'md'
}

function MemberAvatar({ member, size = 'sm' }: MemberAvatarProps) {
  const dimension = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6'
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-xs'

  if (member.avatarUrl) {
    return <img src={member.avatarUrl} alt="" className={cn(dimension, 'rounded-full')} />
  }

  const colors = getAvatarColor(member.userId || member.fullName)
  return (
    <div
      className={cn(
        dimension,
        textSize,
        'rounded-full flex items-center justify-center font-medium',
        colors.bg,
        colors.text
      )}
    >
      {getInitials(member.fullName)}
    </div>
  )
}

export function OwnerPicker({ value, teamMembers, onChange, disabled }: OwnerPickerProps) {
  const [open, setOpen] = useState(false)
  const assignedMember = teamMembers.find((m) => m.userId === value)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {assignedMember ? (
          <button className="flex items-center gap-1.5 text-xs hover:bg-accent/50 rounded px-1.5 py-0.5 transition-colors">
            <MemberAvatar member={assignedMember} />
            <span className="text-xs truncate max-w-[80px]">
              {assignedMember.fullName?.split(' ')[0] ?? 'Assigned'}
            </span>
          </button>
        ) : (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-meta">
            <User className="h-3 w-3 mr-1" />
            Assign
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {teamMembers.map((member) => (
          <DropdownMenuItem
            key={member.userId}
            onClick={() => {
              onChange(member.userId)
              setOpen(false)
            }}
            className={cn(value === member.userId && 'bg-accent')}
          >
            <div className="mr-2">
              <MemberAvatar member={member} />
            </div>
            <span className="text-sm truncate">{member.fullName ?? member.userId}</span>
          </DropdownMenuItem>
        ))}
        {value && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                onChange(null)
                setOpen(false)
              }}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4 mr-2" />
              Unassign
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
